import apiClient from '@/api/client';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface UserResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  language: string;
  is_active: boolean;
  // is_system_admin removed in Sprint 4b (ADR-0001).
  must_change_password?: boolean;
  totp_enabled?: boolean;
  last_login_at: string | null;
  created_at: string;
  preferences?: Record<string, string>;
}

export interface CompanyInfo {
  company_id: string;
  company_name: string;
  company_colour: string;
  // Resolved effective theme (platform default → per-tenant override cascade),
  // computed by the backend and surfaced on /auth/me. Optional so older sessions
  // fall back to the brand-color + balanced defaults.
  theme?: {
    palette: string | null;
    style: 'minimal' | 'balanced' | 'soft';
    dark_default: boolean;
  };
  role_name: string;
  is_group_level: boolean;
  is_tenant_admin?: boolean;
  enabled_modules?: string[];
  permissions?: string[];
  // Console-managed feature flags, resolved effective values, surfaced on
  // /auth/me so the app gates UI on the same flags the backend enforces.
  feature_flags?: Record<string, boolean>;
}

export interface LoginResponse {
  user: UserResponse;
  companies: CompanyInfo[];
  default_homepage?: string;       // resolved route: "/dashboard", "/portal", "/accounting", etc.
  default_company_id?: string;     // which company to auto-select
  message: string;
  /** Set to true when user has TOTP 2FA enabled — frontend must collect code */
  requires_2fa?: boolean;
}

export interface SwitchCompanyResponse {
  message: string;
  company_id: string;
  enabled_modules: string[];
  permissions: string[];
}

export interface InvitePreviewResponse {
  email: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string;
  expires_at: string;
}

export interface AcceptInviteRequest {
  token: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export const authApi = {
  login: (data: LoginRequest) =>
    apiClient.post<LoginResponse>('/auth/login', data),

  demoEnabled: () =>
    apiClient.get<{ enabled: boolean }>('/auth/demo-enabled'),

  logout: () => apiClient.post('/auth/logout'),

  refresh: () => apiClient.post('/auth/refresh'),

  getMe: () => apiClient.get<{ user: UserResponse; companies: CompanyInfo[] }>('/auth/me'),

  changePassword: (data: { current_password: string; new_password: string }) =>
    apiClient.post('/auth/change-password', data),

  switchCompany: (companyId: string) =>
    apiClient.post<SwitchCompanyResponse>('/auth/switch-company', { company_id: companyId }),

  exitTenant: () =>
    apiClient.post<{ message: string; platform_role: string }>('/auth/exit-tenant'),

  platformSsoHandoff: () =>
    apiClient.post<{ url: string; platform_role: string }>('/auth/platform-sso-handoff'),

  forgotPassword: (email: string) =>
    apiClient.post<{ message: string }>('/auth/forgot-password', { email }),

  resetPasswordWithToken: (token: string, new_password: string) =>
    apiClient.post<{ message: string }>('/auth/reset-password-with-token', { token, new_password }),

  verify2FA: (code: string) =>
    apiClient.post<LoginResponse>('/auth/2fa/verify', { code }),

  enroll2FA: () =>
    apiClient.post<{ secret: string; provisioning_uri: string }>('/auth/2fa/enroll'),

  confirm2FA: (code: string) =>
    apiClient.post<{ message: string; recovery_codes: string[] }>('/auth/2fa/confirm', { code }),

  disable2FA: (code: string) =>
    apiClient.post<{ message: string }>('/auth/2fa/disable', { code }),

  loginWith2FARecovery: (code: string) =>
    apiClient.post<LoginResponse>('/auth/2fa/recovery', { code }),

  regenerate2FARecoveryCodes: (code: string) =>
    apiClient.post<{ recovery_codes: string[] }>('/auth/2fa/recovery/regenerate', { code }),

  recoveryCodeStatus: () =>
    apiClient.get<{ remaining: number; totp_enabled: boolean }>('/auth/2fa/recovery/status'),

  updateMe: (data: { first_name?: string; last_name?: string; phone?: string }) =>
    apiClient.patch<{ user: UserResponse }>('/auth/me', data),

  updatePreferences: (data: {
    default_homepage?: string;
    default_module?: string;
    nav_style?: 'flat' | 'grouped';
  }) => apiClient.patch<{ preferences: Record<string, string> }>('/auth/me/preferences', data),

  // OTP verification (signup flow)
  requestOtp: (email: string, first_name: string) =>
    apiClient.post<{ message: string; expires_in: number }>('/auth/request-otp', { email, first_name }),

  verifyOtp: (email: string, code: string) =>
    apiClient.post<{ message: string; verified: boolean }>('/auth/verify-otp', { email, code }),

  resendOtp: (email: string, first_name: string) =>
    apiClient.post<{ message: string; expires_in: number }>('/auth/resend-otp', { email, first_name }),

  invitePreview: (token: string) =>
    apiClient.get<InvitePreviewResponse>('/auth/invite-preview', { params: { token } }),

  acceptInvite: (data: AcceptInviteRequest) =>
    apiClient.post<UserResponse>('/auth/accept-invite', data),
};
